import React from 'react';

import { useTranslation } from 'react-i18next';
import { getTranslatedProduct } from '../../utils/translationUtils';

const ProductDetails = ({ product, loading, error }) => {
  const { t, i18n } = useTranslation();
  const name =
    getTranslatedProduct(product, 'name', i18n.language) ||
    t('product.loading');
  const images = Array.isArray(product?.images) ? product.images : [];
  const fallback = '/assets/product-details-image.webp';
  const image1 = images[1] || images[0] || fallback; // second image preferred
  const image2 = images[2] || images[1] || images[0] || fallback; // third image preferred
  const price = product ? `$${Number(product.price ?? 0).toFixed(2)}` : '—';
  const rawDesc = getTranslatedProduct(product, 'description', i18n.language);
  const firstParagraph = (() => {
    if (!rawDesc) return '';
    const byDoubleNewline = rawDesc
      .split(/\n\s*\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (byDoubleNewline.length > 0) return byDoubleNewline[0];
    const byLine = rawDesc
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (byLine.length > 0) return byLine[0];
    return rawDesc;
  })();
  // Standardized Color Map matching ProductForm
  const colorMap = {
    Black: '#000000',
    Grey: '#9CA3AF',
    Gray: '#9CA3AF',
    White: '#FFFFFF',
    Yellow: '#FDE047',
    Blue: '#1D4ED8',
    Green: '#16A34A',
    'Mint Green': '#86EFAC',
    Brown: '#92400E',
    Pink: '#F472B6',
    Red: '#EF4444',
    'Bi-Color':
      'linear-gradient(135deg, #ff0000, #ffa500, #ffff00, #00ff00, #0000ff, #4b0082, #ee82ee)',
    Champagne: '#F5DEB3',
    Silver: '#D1D5DB',
    Purple: '#6B21A8',
    Navy: '#1E3A8A',
    Orange: '#F97316',
    Beige: '#F5F5DC',
  };

  const colors =
    product?.workwear?.colors && product.workwear.colors.length > 0
      ? product.workwear.colors
      : Array.isArray(product?.colors)
        ? product.colors
        : [];

  const isWorkwear = (product?.category || '').toLowerCase() === 'workwear';

  return (
    <div className="bg-[#F6F6F6] py-10 md:py-[72px] px-5 sm:px-6 xl:px-8">
      <div className="w-full max-w-[1296px] mx-auto space-y-10 md:space-y-[72px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 sm:gap-5">
          <div className="rounded-[10px] h-[400px] md:h-[733px]  bg-[#EDEDED] md:bg-white overflow-hidden flex items-center justify-center relative">
            <div
              className="absolute bottom-0 h-[426px] w-full"
              style={{
                background:
                  'linear-gradient(180deg, rgba(15, 15, 15, 0) 0%, rgba(15, 15, 15, 0.4) 100%)',
              }}
            />
            <img
              src={image1}
              alt={name}
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-5 md:bottom-11 left-[15px] md:left-10 right-[15px] md:right-10  z-10">
              <h2 className="text-white text-[18px] leading-[24px] font-medium mb-5">
                {product?.category || '—'}
              </h2>
              <p className="text-[#fff] text-[14px] leading-[21px] font-medium">
                {firstParagraph}
              </p>
            </div>
          </div>

          <div className="rounded-[10px] h-[400px] md:h-[733px] relative bg-[#EDEDED] md:bg-white overflow-hidden flex items-center justify-center">
            <div
              className="absolute bottom-0 h-[426px] w-full"
              style={{
                background:
                  'linear-gradient(180deg, rgba(15, 15, 15, 0) 0%, rgba(15, 15, 15, 0.4) 100%)',
              }}
            />
            <img
              src={image2}
              alt={name}
              className="w-full h-full object-cover"
            />

            <div className="absolute bottom-5 md:bottom-11 left-[15px] md:left-10 right-[15px] md:right-10 z-10">
              <h2 className="text-white text-[18px] leading-[24px] font-medium mb-5">
                {name}
              </h2>
              <p className="text-[#fff] text-[14px] leading-[21px] font-medium">
                {price}
              </p>
            </div>
          </div>
        </div>
        <div className="py-8 md:py-14 max-w-[891px] mx-auto text-center">
          <p className="text-[#000000] text-[20px] sm:text-[28px] leading-[28px] sm:leading-[42px] font-normal libre-baskerville">
            {firstParagraph}
          </p>
        </div>
        {isWorkwear && colors.length > 0 && (
          <div className="max-w-[891px] mx-auto">
            <div className="flex flex-wrap gap-2 justify-center">
              {colors.map((c) => {
                const isGradient = (colorMap[c] || '').startsWith(
                  'linear-gradient'
                );
                const swatchStyle = isGradient
                  ? { backgroundImage: colorMap[c] }
                  : { background: colorMap[c] || '#E5E7EB' };
                return (
                  <span
                    key={c}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border rounded-full border-gray-300 bg-white"
                  >
                    <span
                      className="inline-block w-4 h-4 rounded-full border border-gray-300"
                      style={swatchStyle}
                    />
                    <span className="text-sm text-gray-800">{c}</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}
        <div className="w-full grid grid-cols-1 lg:grid-cols-[520px_1fr] md:grid-rows-2 gap-5">
          {/* Left - Tall card spanning 2 rows */}
          <div className="rounded-[10px] h-[420px] md:h-[733px] bg-[#EDEDED] overflow-hidden relative md:row-span-2">
            <div
              className="absolute bottom-0 h-[100%] w-full"
              style={{
                background:
                  'linear-gradient(180deg, rgba(15, 15, 15, 0) 0%, rgba(15, 15, 15, 0.45) 100%)',
              }}
            />
            <img
              src="/assets/product-details.webp"
              alt="tall-1"
              className="w-full h-full object-contain"
            />
            <div className="absolute bottom-[35px] md:bottom-[78px] left-5 md:left-[57px] right-5 md:right-8 z-10 max-w-[332px]">
              <h3 className="text-white text-[18px] leading-[24px] font-medium mb-3">
                Lorem
              </h3>
              <p className="text-white/90 text-[14px] leading-[21px] font-medium">
                It is a long established fact that a reader will be distracted
                by the readable conte
              </p>
            </div>
          </div>

          {/* Right - Top card */}
          <div className="rounded-[10px] h-[260px] md:h-[356px] bg-[#EDEDED] overflow-hidden relative w-full">
            <div
              className="absolute bottom-0 h-[55%] w-full"
              style={{
                background:
                  'linear-gradient(180deg, rgba(15, 15, 15, 0) 0%, rgba(15, 15, 15, 0.50) 100%)',
              }}
            />
            <img
              src="/assets/product-details-2.webp"
              alt="wide-1"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-[35px] md:bottom-[62px] left-5 md:left-[62px] right-5 md:right-8 z-10 max-w-[332px]">
              <h3 className="text-white text-[18px] leading-[24px] font-medium mb-3">
                Lorem
              </h3>
              <p className="text-white/90 text-[14px] leading-[21px] font-medium">
                It is a long established fact that a reader will be distracted
                by the readable conte
              </p>
            </div>
          </div>

          {/* Right - Bottom card (centered caption) */}
          <div className="rounded-[10px] h-[260px] md:h-[356px] bg-white overflow-hidden relative w-full">
            <div
              className="absolute bottom-0 h-[55%] w-full"
              style={{
                background:
                  'linear-gradient(180deg, rgba(15, 15, 15, 0) 0%, rgba(15, 15, 15, 0.50) 100%)',
              }}
            />
            <img
              src="/assets/product-details-3.webp"
              alt="wide-2"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-[35px] md:bottom-[78px] left-5 right-5 md:right-[62px] text-left md:w-[332px] z-10">
              <h3 className="text-white text-[18px] leading-[24px] font-medium mb-3">
                Lorem
              </h3>
              <p className="text-white/90 text-[14px] leading-[21px] font-medium">
                It is a long established fact that a reader will be distracted
                by the readable conte
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
